import React, { useEffect, useMemo, useState } from "react"
import moment from "moment"
import Swal from "sweetalert2"
import {
  Button,
  Card,
  CardBody,
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  FormGroup,
  Row,
  Col,
} from "reactstrap"
import { Calendar, momentLocalizer } from "react-big-calendar"
import "react-big-calendar/lib/css/react-big-calendar.css"
import Breadcrumb from "components/Common/Breadcrumb"
import { FaCheckDouble, FaPen } from "react-icons/fa"
import { MdCancel, MdThumbDown, MdThumbUp } from "react-icons/md"
import { TiTick } from "react-icons/ti"
import {
  fetchData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import Loader from "components/Loader/loader"
import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import { usePermissionData } from "AppContext"
import PermittedComponent from "PermittedComponent"
import { FcCancel } from "react-icons/fc"

const getTotalMinutes = (start, end) => {
  if (!start || !end) return 0

  const parse = time => {
    if (!time) return null

    const is12 = /am|pm/i.test(time)
    let h, m

    if (is12) {
      const [t, ap] = time.split(" ")
      ;[h, m] = t.split(":").map(Number)
      if (ap.toLowerCase() === "pm" && h < 12) h += 12
      if (ap.toLowerCase() === "am" && h === 12) h = 0
    } else {
      ;[h, m] = time.split(":").map(Number)
    }

    return h * 60 + (m || 0)
  }

  let startMin = parse(start)
  let endMin = parse(end)

  if (endMin < startMin) endMin += 1440 // next day

  return endMin - startMin
}

const getCurrentWeekRange = () => {
  const today = new Date()

  const start = new Date(today)
  start.setDate(today.getDate() - today.getDay()) // Sunday

  const end = new Date(start)
  end.setDate(start.getDate() + 6) // Saturday

  return { start, end }
}

const AttendanceReport = () => {
  const { checkAccess } = usePermissionData()

  const [visibleRange, setVisibleRange] = useState({ start: null, end: null })
  const [totalHours, setTotalHours] = useState("")

  const [selectedLocation, setSelectedLocation] = useState("")
  const [selectedEmployee, setSelectedEmployee] = useState("")
  const [locationTypeFilter, setLocationTypeFilter] = useState("")

  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)

  const filteredEvents = events.filter(event => {
    /* 📍 Location filter */
    const matchLocation =
      !selectedLocation ||
      String(event?.calender?.locationId ?? event?.calender?.location?.id) ===
        String(selectedLocation)

    /* 👤 Employee filter */
    const matchEmployee =
      !selectedEmployee ||
      String(event?.employee?.id) === String(selectedEmployee)

    /* 🏷️ Type filter (same logic as earlier, FIXED) */
    const locationTypes = (() => {
      try {
        return event?.calender?.location?.type
          ? JSON.parse(event.calender.location.type)
          : []
      } catch {
        return []
      }
    })()

    const matchType =
      !locationTypeFilter?.length ||
      // ✔ match by location type
      locationTypes.some(t => locationTypeFilter.includes(t)) ||
      // ✔ match by employee type
      (locationTypeFilter.includes("window_cleaning") &&
        event?.employee?.isWindowCleaner) ||
      (locationTypeFilter.includes("industrial_cleaning") &&
        event?.employee?.isIndustrialCleaner)

    return matchLocation && matchEmployee && matchType
  })

  // Modal states
  const [editModal, setEditModal] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)

  // Separate state for inputs
  const [startTime, setStartTime] = useState("")
  const [outTime, setoutTime] = useState("")

  const [detailModal, setDetailModal] = useState(false)
  const [selectedEventForDetail, setSelectedEventForDetail] = useState(null)

  useEffect(() => {
    const initialRange = getCurrentWeekRange()
    setVisibleRange(initialRange)
  }, [])

  useEffect(() => {
    getTimeSheet()
  }, [])

  async function getTimeSheet() {
    const APIdata = await fetchData(setLoading, "timesheet")
    if (APIdata?.success) {
      const rawData = APIdata.timesheet.map(item => ({
        ...item,
        start: moment(
          `${item.date} ${item.inTime}`,
          "YYYY-MM-DD HH:mm"
        ).toDate(),
        end: moment(
          `${item.date} ${item.outTime}`,
          "YYYY-MM-DD HH:mm"
        ).toDate(),
      }))
      setEvents(rawData)
    }
  }

  const getEventsInVisibleRange = () => {
    if (!visibleRange.start || !visibleRange.end) return []

    return filteredEvents.filter(ev => {
      if (!ev.start || !ev.end) return false
      return ev.start >= visibleRange.start && ev.start <= visibleRange.end
    })
  }

  const calculateTotalWeekHours = () => {
    const rangeEvents = getEventsInVisibleRange()
    let totalMinutes = 0

    rangeEvents.forEach(ev => {
      const diff = ev.end - ev.start
      totalMinutes += diff / (1000 * 60)
    })

    const hrs = Math.floor(totalMinutes / 60)
    const mins = Math.round(totalMinutes % 60)

    return `${hrs}h ${mins}m`
  }

  useEffect(() => {
    setTotalHours(calculateTotalWeekHours())
  }, [visibleRange, events])

  const convertTo24HourFormat = time12h => {
    if (!time12h) return ""
    const [time, modifier] = time12h.split(" ")
    let [hours, minutes] = time.split(":")

    if (hours === "12") hours = "00"
    if (modifier?.toUpperCase() === "PM")
      hours = String(parseInt(hours, 10) + 12)

    return `${hours.padStart(2, "0")}:${minutes}`
  }

  const calculateTotalHours = (start, end) => {
    if (!start || !end) return ""

    let startDate, endDate

    // Helper to parse time string
    const parseTime = timeStr => {
      if (!timeStr) return null

      let hours, minutes

      // Check if string contains AM/PM
      const is12Hour = /am|pm/i.test(timeStr.trim())

      if (is12Hour) {
        // Example: "2:30 PM"
        const [timePart, ampm] = timeStr.trim().split(" ")
        let [h, m] = timePart.split(":").map(Number)
        if (ampm.toLowerCase() === "pm" && h < 12) h += 12
        if (ampm.toLowerCase() === "am" && h === 12) h = 0
        hours = h
        minutes = m || 0
      } else {
        // 24-hour format "14:30" or "9:05"
        const [h, m] = timeStr.split(":").map(Number)
        hours = h
        minutes = m || 0
      }

      const date = new Date()
      date.setHours(hours, minutes, 0, 0)
      return date
    }

    // Determine startDate
    if (start instanceof Date) startDate = new Date(start)
    else if (typeof start === "string") startDate = parseTime(start)

    // Determine endDate
    if (end instanceof Date) endDate = new Date(end)
    else if (typeof end === "string") endDate = parseTime(end)

    if (!startDate || !endDate) return ""

    // If end is earlier than start → next day
    if (endDate < startDate) endDate.setDate(endDate.getDate() + 1)

    const diffMs = endDate - startDate
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

    return `${diffHrs}h ${diffMins}m`
  }

  // Open modal and set input states from selected event's start/end
  const openEditModal = event => {
    setSelectedEvent(event)
    setStartTime(convertTo24HourFormat(event?.inTime))
    setoutTime(convertTo24HourFormat(event?.outTime))
    setEditModal(true)
  }

  // Close modal and clear states
  const closeEditModal = () => {
    setEditModal(false)
    setSelectedEvent(null)
  }

  const openDetailModal = event => {
    setSelectedEventForDetail(event)
    setDetailModal(true)
  }

  const closeDetailModal = () => {
    setDetailModal(false)
    setSelectedEventForDetail(null)
  }

  const saveTimeChanges = async () => {
    const result = await Swal.fire({
      title: "Are you Sure",
      text: "you want to update the time for this Job",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchPutData(
        setLoading,
        "timesheet",
        selectedEvent?.id,
        {
          inTime: startTime,
          outTime,
        }
      )
      if (APIdata?.data?.success) {
        setEvents(prev =>
          prev.map(ev =>
            ev.id === selectedEvent.id
              ? { ...ev, startTime, outTime, onLeave: false }
              : ev
          )
        )
        Swal.fire("Approved!", "Job has been approved.", "success")
      } else {
        Swal.fire(
          "Something Went Wrong!",
          APIdata?.response?.data?.message,
          "error"
        )
      }
      Swal.fire("Updated!", "Time has been updated.", "success")
      closeEditModal()
    }
  }

  // Confirm Approve job
  const confirmApprove = async event => {
    const result = await Swal.fire({
      title: `Approve job for ${event.employee?.name}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchPutData(setLoading, "timesheet", event?.id, {
        status: true,
      })
      if (APIdata?.data?.success) {
        getTimeSheet()
        Swal.fire("Approved!", "Job has been approved.", "success")
      } else {
        Swal.fire(
          "Something Went Wrong!",
          APIdata?.response?.data?.message,
          "error"
        )
      }
    }
  }

  const confirmPartial = async event => {
    const result = await Swal.fire({
      title: `Approve job Partially for ${event.employee?.name}?`,
      text: "Employee will get Payment For Planned Hours Only",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchPutData(
        setLoading,
        "timesheet/alloted/time",
        event?.id,
        {
          status: true,
        }
      )
      if (APIdata?.data?.success) {
        getTimeSheet()
        Swal.fire("Approved!", "Job has been approved Partially.", "success")
      } else {
        Swal.fire(
          "Something Went Wrong!",
          APIdata?.response?.data?.message,
          "error"
        )
      }
    }
  }

  // Confirm Cancel job
  const confirmCancel = async event => {
    const result = await Swal.fire({
      title: `Cancel job for ${event.employee?.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cancel Job",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchPutData(setLoading, "timesheet", event?.id, {
        status: false,
      })
      if (APIdata?.data?.success) {
        getTimeSheet()
        Swal.fire("Disapproved!", "Job has been disapproved.", "success")
      } else {
        Swal.fire(
          "Something Went Wrong!",
          APIdata?.response?.data?.message,
          "error"
        )
      }
    }
  }

  const handleGlobalApprove = async () => {
    const result = await Swal.fire({
      title: "Approve all Timesheets?",
      text: "This will approve all Timesheets in the calendar.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, approve all",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchPostData(
        setLoading,
        "timesheet/bulk/approval",
        {
          fromDate: moment(visibleRange?.start).format("YYYY-MM-DD"),
          toDate: moment(visibleRange?.end).format("YYYY-MM-DD"),
          status: true,
        }
      )
      if (APIdata?.data?.success) {
        getTimeSheet()
        Swal.fire("Approved!", APIdata?.data?.message, "success")
      } else {
        Swal.fire(
          "Something went wrong",
          APIdata?.response?.data?.message,
          "error"
        )
      }
    }
  }

  const handleGlobalCancel = async () => {
    const result = await Swal.fire({
      title: "Cancel all Timesheets?",
      text: "This will reject all timesheets in the calendar.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel all",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchPostData(
        setLoading,
        "timesheet/bulk/approval",
        {
          fromDate: moment(visibleRange?.start).format("YYYY-MM-DD"),
          toDate: moment(visibleRange?.end).format("YYYY-MM-DD"),
          status: false,
        }
      )
      if (APIdata?.data?.success) {
        getTimeSheet()
        Swal.fire("Rejected!", APIdata?.data?.message, "success")
      } else {
        Swal.fire(
          "Something went wrong",
          APIdata?.response?.data?.message,
          "error"
        )
      }
    }
  }

  const uniqueLocations = [
    ...new Map(
      filteredEvents.map(e => [
        e?.calender?.location?.id,
        e?.calender?.location,
      ])
    ).values(),
  ]

  const uniqueEmployees = [
    ...new Map(
      filteredEvents?.map(e => [e?.employee?.id, e?.employee])
    ).values(),
  ]

  // Custom event rendering
  const EventComponent = info => {
    const fcEvent = info.event
    const event = fcEvent.extendedProps
    // Safety check
    if (!event && !fcEvent.id) return null

    const timeAdded = event.inTime && event.outTime

    console.log(event)

    const plannedMinutes = getTotalMinutes(
      event?.calender?.inTime,
      event?.calender?.outTime
    )

    const actualMinutes = getTotalMinutes(event?.inTime, event?.outTime)

    let backgroundColor =
      event.status === "0"
        ? "#888"
        : event.status === "1"
        ? " #4caf50"
        : "#3d6eb6"
    // 🟡 Less than planned

    if (actualMinutes < plannedMinutes && event?.status === null) {
      backgroundColor = "#f1b44c"
    }

    const totalHours = timeAdded
      ? calculateTotalHours(event.inTime, event.outTime)
      : "Not Added"

    return (
      <div
        className="text-white p-2 overflow-hidden w-100"
        onClick={e => {
          if (!e.target.closest("button")) {
            openDetailModal(event) // pass extendedProps
          }
        }}
        style={{
          cursor: "pointer",
          backgroundColor,
        }}
      >
        <div>
          <p className="mb-2 text-start" style={{ fontSize: "10.5px" }}>
            {event.inTime} - {event.outTime} | {totalHours}
          </p>
          <h6 className="my-1 fw-bold text-start" style={{ fontSize: "12px" }}>
            {event?.calender?.location?.name}
          </h6>
          <div className="medium text-start" style={{ fontSize: "12px" }}>
            {event?.employee?.name} (£{event?.employee?.hourlyRate}/hr)
          </div>
        </div>

        <div className="mt-3 d-flex gap-2">
          {!timeAdded && (
            <span className="badge bg-danger">Time Not Added</span>
          )}

          {timeAdded && (
            <>
              <Button
                size="sm"
                className="bg-black px-2"
                onClick={e => {
                  e.stopPropagation()
                  openDetailModal({
                    ...event,
                    id: fcEvent.id,
                    title: fcEvent.title,
                    start: fcEvent.start,
                    end: fcEvent.end,
                  })
                }}
              >
                <FaPen />
              </Button>

              {event.status !== "1" && (
                <Button
                  size="sm"
                  color="success"
                  className="px-2"
                  onClick={e => {
                    e.stopPropagation()
                    confirmApprove({
                      ...event,
                      id: fcEvent.id,
                      title: fcEvent.title,
                      start: fcEvent.start,
                      end: fcEvent.end,
                    })
                  }}
                >
                  <TiTick size={15} />
                </Button>
              )}

              {actualMinutes < plannedMinutes && event?.status === null && (
                <Button
                  size="sm"
                  color="success"
                  className="px-2"
                  onClick={e => {
                    e.stopPropagation()
                    confirmPartial({
                      ...event,
                      id: fcEvent.id,
                      title: fcEvent.title,
                      start: fcEvent.start,
                      end: fcEvent.end,
                    })
                  }}
                >
                  <FaCheckDouble size={15} />
                </Button>
              )}

              {actualMinutes > plannedMinutes && event?.status === null && (
                <Button
                  size="sm"
                  color="danger"
                  className="px-2"
                  onClick={e => {
                    e.stopPropagation()
                    confirmPartial({
                      ...event,
                      id: fcEvent.id,
                      title: fcEvent.title,
                      start: fcEvent.start,
                      end: fcEvent.end,
                    })
                  }}
                >
                  <MdCancel size={15} />
                </Button>
              )}

              {event.status !== "0" && (
                <Button
                  size="sm"
                  color="light"
                  className="px-2"
                  onClick={e => {
                    e.stopPropagation()
                    confirmCancel({
                      ...event,
                      id: fcEvent.id,
                      title: fcEvent.title,
                      start: fcEvent.start,
                      end: fcEvent.end,
                    })
                  }}
                >
                  <FcCancel size={15} />
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="page-content px-1">
      <PermittedComponent isAccess={checkAccess(2, 1) || checkAccess(2, 2)}>
        {loading && <Loader />}

        <Container fluid className="px-0">
          <div className="px-4">
            <Breadcrumb
              title={"Dashboards"}
              breadcrumbItem={"Timesheet Report"}
              description={"View and Manage Timesheet of Employees"}
            />
          </div>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5>Employee Timesheet Calendar</h5>
                <div className="d-flex align-items-center justify-content-end gap-3">
                  <Card className="mb-0 bg-blue py-2 px-4 rounded-3 shadow">
                    <h6 className="mb-0 fs-6 p-1">
                      Total Working Hrs : <b>{totalHours}</b>
                    </h6>
                  </Card>
                  <div className="d-flex gap-3">
                    <Button
                      color="success"
                      className="fw-semibold"
                      onClick={handleGlobalApprove}
                    >
                      <MdThumbUp size={16} className="me-2" /> Approve All
                    </Button>
                    <Button
                      color="danger"
                      className="fw-semibold"
                      onClick={handleGlobalCancel}
                    >
                      <MdThumbDown size={16} className="me-2" /> Reject All
                    </Button>
                  </div>
                </div>
                <div className="d-flex gap-3">
                  <select
                    className="form-select rounded-4 ps-3 pe-5 w-auto"
                    value={locationTypeFilter}
                    onChange={e => {
                      setLocationTypeFilter(e.target.value)
                    }}
                  >
                    <option value="">All (Both Type)</option>
                    <option value="window_cleaning">Window Cleaning</option>
                    <option value="industrial_cleaning">
                      Industrial Cleaning
                    </option>
                  </select>
                  <select
                    className="form-select rounded-4"
                    value={selectedLocation}
                    onChange={e => setSelectedLocation(e.target.value)}
                  >
                    <option value="">Select Location</option>
                    {uniqueLocations
                      .filter(i => i?.id)
                      .map(loc => (
                        <option key={loc?.id} value={loc?.id}>
                          {loc?.name}
                        </option>
                      ))}
                  </select>
                  <select
                    className="form-select rounded-4"
                    value={selectedEmployee}
                    onChange={e => setSelectedEmployee(e.target.value)}
                  >
                    <option value="">Select Employee</option>
                    {uniqueEmployees
                      .filter(i => i.id)
                      .map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <Card>
                <CardBody>
                  <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin]}
                    initialView="dayGridWeek"
                    events={filteredEvents}
                    eventContent={EventComponent}
                    dayHeaderFormat={{
                      weekday: "short",
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    }}
                    headerToolbar={{
                      left: "prev,next today",
                      right: "title",
                      // Buttons: Day, Week, Month
                    }}
                    views={{
                      timeGridWeek: {
                        buttonText: "Week",
                        dayHeaderFormat: {
                          weekday: "short",
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        },
                      },
                      timeGridDay: {
                        buttonText: "Day",
                        dayHeaderFormat: {
                          weekday: "short",
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        },
                      },
                      dayGridMonth: {
                        buttonText: "Month",
                      },
                    }}
                  />
                </CardBody>
              </Card>
            </CardBody>
          </Card>
        </Container>

        {/* Edit Time Modal */}
        <Modal isOpen={editModal} toggle={closeEditModal} centered>
          <ModalHeader toggle={closeEditModal}>
            Edit Time for {selectedEvent?.employee?.name}
          </ModalHeader>
          <ModalBody
            className="pb-0"
            style={{ paddingBottom: "0px !importent" }}
          >
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="startTime">
                    Start Time <span className="text-danger">*</span>
                  </Label>
                  <input
                    id="startTime"
                    type="time"
                    className="form-control"
                    value={startTime}
                    onChange={e => setStartTime(e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="outTime">
                    End Time <span className="text-danger">*</span>
                  </Label>
                  <input
                    id="outTime"
                    type="time"
                    className="form-control"
                    value={outTime}
                    onChange={e => setoutTime(e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="outTime">Total Hrs</Label>
                  <input
                    id="outTime"
                    type="text"
                    disabled
                    className="form-control"
                    value={calculateTotalHours(startTime, outTime)}
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={saveTimeChanges}>
              Save
            </Button>{" "}
            <Button color="secondary" onClick={closeEditModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        <Modal
          isOpen={detailModal}
          toggle={closeDetailModal}
          centered
          size="lg"
        >
          <ModalHeader toggle={closeDetailModal}>
            Timesheet Details - {selectedEventForDetail?.employee?.name}
          </ModalHeader>
          <ModalBody className="bg-white">
            {selectedEventForDetail && (
              <Row>
                <Col md={5}>
                  <div className="mb-4">
                    <strong>Date:</strong>{" "}
                    {moment(selectedEventForDetail.start).format(
                      "dddd, MMMM Do YYYY"
                    )}
                  </div>
                  <div className="mb-4">
                    <strong>Employee:</strong>{" "}
                    {selectedEventForDetail?.employee?.name}
                  </div>
                  <div className="mb-4">
                    <strong>Hourly Rate:</strong> £
                    {selectedEventForDetail?.employee?.hourlyRate} / hr
                  </div>
                  <div className="mb-4">
                    <strong>Status:</strong>{" "}
                    <span
                      className={`badge p-2 bg-${
                        selectedEventForDetail.status === "1"
                          ? "success"
                          : selectedEventForDetail.status === "0"
                          ? "secondary"
                          : "warning"
                      }`}
                    >
                      {selectedEventForDetail.status === "1"
                        ? "Approved"
                        : selectedEventForDetail.status === "0"
                        ? "Rejected"
                        : "Pending"}
                    </span>
                  </div>
                </Col>
                <Col md={7}>
                  <div className="mb-4">
                    <strong>Location:</strong>{" "}
                    {selectedEventForDetail?.calender?.location?.name}
                  </div>
                  <div className="mb-4 d-flex justify-content-between">
                    <div>
                      <strong>InTime:</strong>{" "}
                      {selectedEventForDetail.inTime
                        ? moment(
                            `${selectedEventForDetail?.calender?.date} ${selectedEventForDetail.inTime}`
                          ).format("h:mm A")
                        : "Not Added"}
                    </div>
                    <div>
                      <strong>OutTime:</strong>{" "}
                      {selectedEventForDetail.outTime
                        ? moment(
                            `${selectedEventForDetail?.calender.date} ${selectedEventForDetail.outTime}`
                          ).format("h:mm A")
                        : "Not Added"}
                    </div>
                    <div>
                      <strong>Total Hrs:</strong>{" "}
                      {selectedEventForDetail.inTime &&
                      selectedEventForDetail.outTime
                        ? calculateTotalHours(
                            selectedEventForDetail.inTime,
                            selectedEventForDetail.outTime
                          )
                        : "Not Calculated"}
                    </div>
                  </div>
                  <div className="mb-4 d-flex gap-2">
                    <p className="fw-bold mb-0">Punched In Address :</p>{" "}
                    <p className="d-inlineblock mb-0">
                      {selectedEventForDetail.punchInAddress}
                    </p>
                  </div>
                  <div className="mb-2 d-flex gap-2">
                    <p className="fw-bold mb-0">Punched Out Address :</p>{" "}
                    <p className="d-inlineblock mb-0">
                      {selectedEventForDetail.punchOutAddress}
                    </p>
                  </div>
                </Col>
              </Row>
            )}
          </ModalBody>
          <ModalFooter className="bg-white">
            {selectedEventForDetail?.start && selectedEventForDetail?.end && (
              <>
                <Button
                  color="primary"
                  onClick={() => {
                    closeDetailModal()
                    openEditModal(selectedEventForDetail)
                  }}
                >
                  <FaPen className="me-2" /> Edit Time
                </Button>
                <Button
                  color="success"
                  onClick={() => {
                    closeDetailModal()
                    confirmApprove(selectedEventForDetail)
                  }}
                >
                  Approve
                </Button>
                <Button
                  color="danger"
                  onClick={() => {
                    closeDetailModal()
                    confirmCancel(selectedEventForDetail)
                  }}
                >
                  Reject
                </Button>
              </>
            )}
            <Button color="secondary" onClick={closeDetailModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </PermittedComponent>
    </div>
  )
}

export default AttendanceReport
